import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api/baseQuery';
import type { OrganizationSummary, PaginatedResponse } from '@core/types/api';

/**
 * Accept both a bare list and a DRF pagination envelope.
 *
 * Whether a read-only collection opts out of pagination (`pagination_class =
 * None`) is a backend detail; unwrapping here means the SPA keeps working if it
 * ever changes.
 */
function unwrapList<T>(response: unknown): T[] {
  if (Array.isArray(response)) {
    return response as T[];
  }
  return (response as PaginatedResponse<T> | null)?.results ?? [];
}

/**
 * Organization API using RTK Query.
 *
 * `listOrganizations` is USER-scoped — it describes the caller's memberships (or
 * every organization, for a superuser), so it must NOT be reset when the active
 * organization changes. Organization-scoped APIs (filtered by the
 * `X-Organization` header the base query attaches) belong to their own feature
 * and must be listed in the store's `TENANT_SCOPED_APIS`.
 */
export const organizationApi = createApi({
  reducerPath: 'organizationApi',
  baseQuery,
  endpoints: (builder) => ({
    listOrganizations: builder.query<OrganizationSummary[], void>({
      query: () => ({
        url: '/api/accounts/organizations/',
        method: 'GET',
      }),
      transformResponse: (response: unknown) => unwrapList<OrganizationSummary>(response),
    }),
  }),
});

export const { useListOrganizationsQuery } = organizationApi;
